import AchClient, { AchItem } from '../../ach/AchClient';
import HttpClient from '../../../gateway/HttpClient';
import { notBlank, notUndefined } from '../../../util/Assert';
import HttpMethod from '../../../constant/HttpMethod';
import { AchMethods, AlarmMethods } from '../../../constant/MethodNames';
import { query } from '@trlogic/client-js';
import { TransactionData } from '../../../domain/stream/transaction/TransactionDomains';
import { GetAchOptions } from '../../../service/AchService';
import { CloseCaseBulkActionOperation } from '../../../domain/case/CaseDomains';
import { TransactionDataExportJob } from '../../../domain/job/JobDomain';
import SelectQuery = query.SelectQuery;

class NestAchClient implements AchClient {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    notUndefined(httpClient, 'Http client cannot be undefined');
    this.httpClient = httpClient;
  }

  public uploadAchFile(file: File): Promise<string> {
    notUndefined(file, 'File cannot be undefined');

    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient.execute<string>(HttpMethod.POST, AchMethods.UPLOAD, formData);
  }

  public getAchFiles(query: SelectQuery): Promise<AchItem[]> {
    notUndefined(query, 'Query cannot be undefined');

    return this.httpClient.execute<AchItem[]>(HttpMethod.GET, AchMethods.GET, undefined, { query: JSON.stringify(query) });
  }

  public getAchFileCount(query: SelectQuery): Promise<number> {
    notUndefined(query, 'Query cannot be undefined');

    return this.httpClient.execute<number>(HttpMethod.GET, AchMethods.COUNT, undefined, { query: JSON.stringify(query) });
  }

  public getTransactionsByFileName(fileName: string, query: SelectQuery): Promise<{ data: TransactionData[]; count: number }> {
    notBlank(fileName, 'FileName cannot be blank');
    notUndefined(query, 'Query cannot be undefined');

    const encodedFileName = encodeURIComponent(fileName);
    return this.httpClient.execute<{ data: TransactionData[]; count: number }>(
      HttpMethod.GET,
      `${AchMethods.GET_TRANSACTIONS_BY_FILE_NAME}/${encodedFileName}`,
      undefined,
      { query: JSON.stringify(query) },
    );
  }

  public getScores(domain: string, options?: GetAchOptions): Promise<Record<string, number>> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');

    return this.httpClient.execute<Record<string, number>>(HttpMethod.GET, AlarmMethods.GET_SCORES, undefined, { domain, options });
  }

  public closeBulk(domain: string, ids: CloseCaseBulkActionOperation[], type: string, reason?: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');

    const closeBulkCaseDto = {
      domain: domain,
      ids: ids,
      type: type,
      reason: reason,
    };
    return this.httpClient.execute<void>(HttpMethod.POST, AlarmMethods.CLOSE_BULK, closeBulkCaseDto);
  }

  public assign(domain: string, index: string, assign: string | null): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');

    const assignDto = {
      domain: domain,
      index: index,
      assign: assign,
    };
    return this.httpClient.execute<void>(HttpMethod.POST, AlarmMethods.ASSIGN, assignDto);
  }

  public closeCase(domain: string, index: string, type: string, reason?: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');

    const closeCaseDto = {
      domain: domain,
      index: index,
      type: type,
      reason: reason,
    };
    return this.httpClient.execute<void>(HttpMethod.POST, AlarmMethods.CLOSE_CASE, closeCaseDto);
  }

  public holdCase(domain: string, index: string, reason?: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');

    const holdCaseDto = {
      domain: domain,
      index: index,
      reason: reason,
    };
    return this.httpClient.execute<void>(HttpMethod.POST, AlarmMethods.HOLD_CASE, holdCaseDto);
  }

  public getEnrichmentVariable(enrichments: string[], transactionData: TransactionData): Promise<Record<string, any>> {
    notUndefined(enrichments, 'Enrichments cannot be undefined');
    notUndefined(transactionData, 'TransactionData cannot be undefined');

    const body = {
      enrichments,
      transactionData,
    };
    return this.httpClient.execute<Record<string, any>>(HttpMethod.POST, AlarmMethods.GET_ENRICHMENT_VARIABLE, body);
  }

  public exportTransactions(job: TransactionDataExportJob): Promise<void> {
    notUndefined(job, 'Job cannot be undefined');
    return this.httpClient.execute<void>(HttpMethod.POST, AchMethods.EXPORT, job);
  }
}

export default NestAchClient;
